import React from "react";
import {
  Box,
  Typography,
  Stepper as MuiStepper,
  StepContent,
  Step,
  StepLabel,
  styled,
} from "@mui/material";
import { StepIconProps } from "@mui/material/StepIcon";
import { Stack } from "@mui/system";

export interface StepperProps {
  steps: {
    value: number;
    label: string;
    heading: string;
    description: string;
  }[];
}
const StyledLabel = styled(StepLabel, {
  shouldForwardProp: (prop: any) => prop !== "active",
})<{ active?: boolean | undefined; theme?: any }>(({ active, theme }) => ({
  marginLeft: active ? 8 : 24,
  padding: 0,
  "& .MuiTypography-root": {
    fontSize: active ? "36px" : "24px",
    color: active
      ? theme.palette.primary.main
      : theme.palette.common.borderGrey,
  },
}));

const StyledContent = styled(StepContent, {
  shouldForwardProp: (prop: any) => prop !== "active",
})<{ active?: boolean | undefined }>(({ active, theme }) => ({
  paddingRight: active ? "26px" : 0,
  paddingLeft: active ? "38px" : 0,
  marginLeft: active ? "30px" : "48px",
  borderLeft: active ? `1px solid ${theme.palette.primary.main}` : 0,
  borderLeftWidth: active ? "2px" : 0,
}));

const StyledStepper = styled(MuiStepper)(() => ({
  "& .MuiStepConnector-root": {
    marginLeft: "30px",
    marginTop: "-27px",
    height: "38px",
  },
  "& .MuiStepConnector-line": {
    minHeight: "48px",
    borderLeftWidth: "2px",
    borderColor: "rgba(229, 229, 229, 1)",
  },
}));

const StyledStep = styled(Step)(() => ({
  "& .MuiStep-root": {
    marginLeft: "18px",
  },
}));
const BorderedStack = styled(Step)(() => ({
  borderLeft: ` 2px solid rgba(229, 229, 229, 1)`,
  height: "51px",
  marginLeft: "30px",
  marginTop: "-17px",
}));

const ColorlibStepIconRoot = styled("div")<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ ownerState, theme }: any) => ({
  backgroundColor: ownerState.active
    ? theme.palette.primary.main
    : "rgba(229, 229, 229, 1)",
  color: "#fff",
  width: ownerState.active ? 50 : 14,
  height: ownerState.active ? 50 : 14,
  backgroundImage: ownerState.active
    ? theme.palette.primary.main
    : ownerState.completed
    ? theme.palette.primary.main
    : "none",
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
}));
const Stepper = ({ steps }: StepperProps) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const ColorlibStepIcon = (props: StepIconProps) => {
    const { active, completed, className } = props;

    return (
      <ColorlibStepIconRoot
        ownerState={{ completed, active }}
        className={className}
      >
        {active && <i className="far fa-calendar" />}
      </ColorlibStepIconRoot>
    );
  };

  return (
    <Box position="relative">
      <StyledStepper activeStep={activeStep} orientation="vertical">
        {steps &&
          steps.map((step, index) => {
            const stepProps: { active?: boolean } = {
              active: activeStep === index,
            };

            return (
              <StyledStep
                key={step.value}
                onClick={() => setActiveStep(step.value)}
                {...stepProps}
              >
                <StyledLabel
                  active={stepProps.active}
                  StepIconComponent={ColorlibStepIcon}
                  onClick={() => setActiveStep(step.value)}
                >
                  <Typography
                    fontSize={30}
                    color="primary"
                    lineHeight="36.31px"
                  >
                    {step.label}
                  </Typography>
                </StyledLabel>
                <StyledContent active={stepProps.active}>
                  <Typography
                    variant="h4"
                    fontWeight={600}
                    lineHeight="31.14px"
                  >
                    {step.heading}
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={400}
                    lineHeight="31.14px"
                  >
                    {step.description}
                  </Typography>
                </StyledContent>
              </StyledStep>
            );
          })}
      </StyledStepper>
      <BorderedStack />
      <Stack position="absolute" left="24.3px" bottom="-14px">
        <i className="fal fa-arrow-down font-borderGrey" />
      </Stack>
    </Box>
  );
};
export default Stepper;
